import './Login.css';
import React, { useState } from 'react';
import { Form, useChanges } from '../../components/Form/Form';
import { TextField } from '../../components/Form/TextField/TextField';
import { history } from '../../model/history';
import { resetPassword, confirmPasswordReset, createSession } from '../../model/session';
import { Button } from '../../components/Button/Button';
import { DBInspector } from '../../components/DBInspector/DBInspector';
import { Icon } from '../../components/Icon/Icon';
import { dashboardPath } from '../../routes';
import { I18n, t } from '../../locales/translate';
import { Gatekeeper } from '../Gatekeeper/Gatekeeper';
import { Stack } from '../../components/Stack/Stack';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

export function ForgotPassword(): JSX.Element {
  const resetToken = history.location.search.split('=')[1];

  const [, setChanges, model] = useChanges({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  async function handleOnSubmit(): Promise<void> {
    try {
      setLoading(true);
      if (resetToken) {
        const userEmail = (await confirmPasswordReset({ ...model, resetToken })).email;
        if (userEmail) {
          try {
            await createSession({
              language: I18n.locale,
              email: userEmail,
              password: model.password,
              rememberLogin: false,
            });
          } catch (e) {
            setFormError(t.errors.submitError);
          }
          history.push(dashboardPath());
        }
      } else {
        await resetPassword(model);
      }
      setRequestSubmitted(true);
    } catch (e) {
      const customError = e as { response: { status: number } };
      if (customError.response.status) {
        switch (customError.response.status) {
          case 404: {
            setFormError(t.errors.invalidLink);
            break;
          }
          default: {
            setFormError(t.errors.generalError);
            break;
          }
        }
      } else {
        setFormError(t.errors.offline);
      }
    }
    setLoading(false);
  }

  return (
    <Gatekeeper>
      <Form model={model} onSubmit={handleOnSubmit} onInput={(e) => setChanges(e)} autoComplete>
        <Collapse open={!!formError}>
          <div className="login-form__invalid">
            <span>{formError}</span>
            <Icon name="remove" onClick={() => setFormError('')} />
          </div>
        </Collapse>
        <Stack size="sm">
          <h1>{t.page.login.resetPassword}</h1>
          {resetToken ? (
            <div className="login-form__content">
              <div>
                <TextField name="password" label={t.page.login.password} icon="lock--sm" required type="password" />
                <TextField
                  name="confirmPassword"
                  label={t.page.login.confirmPassword}
                  icon="lock--sm"
                  type="password"
                  errorMessage={
                    model.password !== model.confirmPassword && model.confirmPassword !== ''
                      ? t.errors.passwordMismatch
                      : ''
                  }
                />
              </div>
            </div>
          ) : (
            <div className="login-form__content">
              {!requestSubmitted ? (
                <TextField
                  name="email"
                  label={t.model.user.email}
                  type="email"
                  icon="at-sign--sm"
                  autoFocus={true}
                  required
                />
              ) : (
                t.page.login.checkMail
              )}
            </div>
          )}
          <footer>
            <Button type="primary" block title={t.page.login.resetPassword} loading={loading} isSubmit />
          </footer>
        </Stack>
      </Form>
      <DBInspector />
    </Gatekeeper>
  );
}
